// All HTTP I/O goes through this module so JWT injection + 401 handling is in
// one place. The Vite dev server proxies /api -> the api-gateway.

const TOKEN_KEY = "aice.access_token";
const ACTING_FIRM_KEY = "aice.acting_firm";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null): void {
  if (token === null) localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, token);
}

// Company switcher: which firm a SUPERADMIN is currently "acting as". null = the
// platform-wide aggregate ("All firms"). Ignored by the backend for non-SUPERADMIN
// tokens, so it can only ever narrow the platform operator's own view.
export function getActingFirmId(): string | null {
  return localStorage.getItem(ACTING_FIRM_KEY);
}

export function setActingFirmId(firmId: string | null): void {
  if (firmId === null || firmId === "") localStorage.removeItem(ACTING_FIRM_KEY);
  else localStorage.setItem(ACTING_FIRM_KEY, firmId);
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  opts: { platform?: boolean } = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  // When a SUPERADMIN has picked a firm in the switcher, scope the call to it.
  // `platform: true` opts out — used by platform-management calls (firm registry
  // and per-firm settings) which must always run in the operator context.
  if (!opts.platform) {
    const acting = getActingFirmId();
    if (acting && parseTokenClaims()?.role === "SUPERADMIN") {
      headers["X-AICE-Acting-Firm"] = acting;
    }
  }

  const response = await fetch(path, { ...init, headers });
  if (response.status === 401) {
    setToken(null);
    throw new ApiError(401, "session expired");
  }
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface LoginResponse {
  access_token: string;
  expires_in: number;
  user_id: string;
  firm_id: string;
  role: string;
}

export interface AlertRecord {
  event_id: string;
  firm_id: string;
  vehicle_id: string;
  code: string;
  summary: string;
  severity: string;
  status: string;
  occurred_at: string;
}

export interface NotificationTarget {
  id: string;
  firm_id: string;
  channel: string;
  destination: string;
  active: boolean;
}

export async function login(username: string, password: string): Promise<LoginResponse> {
  const body = await apiFetch<LoginResponse>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  setToken(body.access_token);
  return body;
}

export async function listAlerts(): Promise<AlertRecord[]> {
  return apiFetch<AlertRecord[]>("/api/v1/alerts");
}

export async function acknowledgeAlert(eventId: string): Promise<AlertRecord> {
  return apiFetch<AlertRecord>(`/api/v1/alerts/${encodeURIComponent(eventId)}/acknowledge`, {
    method: "POST",
  });
}

export async function resolveAlert(eventId: string): Promise<AlertRecord> {
  return apiFetch<AlertRecord>(`/api/v1/alerts/${encodeURIComponent(eventId)}/resolve`, {
    method: "POST",
  });
}

export async function listTargets(firmId?: string): Promise<NotificationTarget[]> {
  const qs = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<NotificationTarget[]>(`/api/v1/notifications/targets${qs}`);
}

export async function upsertTarget(
  firmId: string,
  channel: string,
  destination: string,
  active = true,
): Promise<NotificationTarget> {
  return apiFetch<NotificationTarget>("/api/v1/notifications/targets", {
    method: "PUT",
    body: JSON.stringify({
      firm_id: firmId,
      channel,
      destination,
      active,
    }),
  });
}

// --- Registry (firm -> fleet -> vehicle -> device) ---

export interface RegistryFirm {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface RegistryFleet {
  id: string;
  firm_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface RegistryVehicle {
  id: string;
  firm_id: string;
  fleet_id: string | null;
  plate: string | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RegistryDevice {
  id: string;
  firm_id: string;
  vehicle_id: string | null;
  hardware_id: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export async function listRegistryFirms(): Promise<RegistryFirm[]> {
  return apiFetch<RegistryFirm[]>("/api/v1/registry/firms");
}

export async function upsertRegistryFirm(
  firm: Pick<RegistryFirm, "id" | "name">,
): Promise<RegistryFirm> {
  return apiFetch<RegistryFirm>("/api/v1/registry/firms", {
    method: "PUT",
    body: JSON.stringify(firm),
  });
}

export async function listRegistryFleets(firmId?: string): Promise<RegistryFleet[]> {
  const qs = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<RegistryFleet[]>(`/api/v1/registry/fleets${qs}`);
}

export async function upsertRegistryFleet(
  fleet: Pick<RegistryFleet, "id" | "firm_id" | "name">,
): Promise<RegistryFleet> {
  return apiFetch<RegistryFleet>("/api/v1/registry/fleets", {
    method: "PUT",
    body: JSON.stringify(fleet),
  });
}

export async function listRegistryVehicles(firmId?: string): Promise<RegistryVehicle[]> {
  const qs = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<RegistryVehicle[]>(`/api/v1/registry/vehicles${qs}`);
}

export async function upsertRegistryVehicle(
  vehicle: Omit<RegistryVehicle, "created_at" | "updated_at">,
): Promise<RegistryVehicle> {
  return apiFetch<RegistryVehicle>("/api/v1/registry/vehicles", {
    method: "PUT",
    body: JSON.stringify(vehicle),
  });
}

export async function listRegistryDevices(params?: {
  firm_id?: string;
  vehicle_id?: string;
}): Promise<RegistryDevice[]> {
  const qs = new URLSearchParams();
  if (params?.firm_id) qs.set("firm_id", params.firm_id);
  if (params?.vehicle_id) qs.set("vehicle_id", params.vehicle_id);
  const q = qs.toString();
  return apiFetch<RegistryDevice[]>(
    `/api/v1/registry/devices${q ? `?${q}` : ""}`,
  );
}

export async function upsertRegistryDevice(
  device: Omit<RegistryDevice, "created_at" | "updated_at">,
): Promise<RegistryDevice> {
  return apiFetch<RegistryDevice>("/api/v1/registry/devices", {
    method: "PUT",
    body: JSON.stringify(device),
  });
}

// --- Users (ADMIN role required on the gateway for writes) ---

export type UserRole = "SUPERADMIN" | "ADMIN" | "OPERATOR" | "DRIVER" | "VIEWER";

export interface UserRecord {
  id: string;
  firm_id: string;
  username: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  role: UserRole;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export async function listUsers(firmId?: string): Promise<UserRecord[]> {
  const qs = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<UserRecord[]>(`/api/v1/users${qs}`);
}

export async function inviteUser(
  input: {
    firm_id: string;
    username: string;
    display_name?: string;
    email?: string;
    phone?: string;
    role: UserRole;
    // Optional: when omitted and an e-mail is supplied, the backend mints a
    // single-use invite link instead of an admin-set password.
    password?: string;
  },
  // `platform: true` when the caller targets an explicit firm_id regardless of
  // the active company switcher (used by the Firms page first-owner form).
  opts: { platform?: boolean } = {},
): Promise<UserRecord & { invitation_email_sent?: boolean | null }> {
  return apiFetch<UserRecord & { invitation_email_sent?: boolean | null }>(
    "/api/v1/users",
    {
      method: "PUT",
      body: JSON.stringify({ ...input, active: true }),
    },
    opts,
  );
}

// Edit an existing user (same upsert endpoint, keyed by id). Password is left
// untouched when omitted. `platform: true` so a SUPERADMIN's active company
// switcher never rewrites the target user's firm_id — the user's own firm is
// preserved. Only a SUPERADMIN may set role=SUPERADMIN (backend enforces this;
// a firm ADMIN gets 403).
export async function updateUser(
  user: UserRecord,
  changes: Partial<Pick<UserRecord, "role" | "display_name" | "email" | "phone" | "active">>,
): Promise<UserRecord> {
  return apiFetch<UserRecord>(
    "/api/v1/users",
    {
      method: "PUT",
      body: JSON.stringify({
        id: user.id,
        firm_id: user.firm_id,
        username: user.username,
        display_name: changes.display_name ?? user.display_name,
        email: changes.email ?? user.email,
        phone: changes.phone ?? user.phone,
        role: changes.role ?? user.role,
        active: changes.active ?? user.active,
        send_invitation_email: false,
      }),
    },
    { platform: true },
  );
}

export async function deleteUser(userId: string): Promise<void> {
  await apiFetch<void>(`/api/v1/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
}

export interface ProfileRecord {
  id: string;
  firm_id: string;
  username: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  role: string;
}

export async function getProfile(): Promise<ProfileRecord> {
  return apiFetch<ProfileRecord>("/api/v1/users/me");
}

export async function updateProfile(input: {
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
}): Promise<ProfileRecord> {
  return apiFetch<ProfileRecord>("/api/v1/users/me", {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

// --- Per-firm notification settings ---

export interface FirmNotificationSettings {
  firm_id: string;
  smtp_host?: string | null;
  smtp_port?: number | null;
  smtp_username?: string | null;
  smtp_password?: string | null;
  smtp_sender?: string | null;
  smtp_use_tls?: boolean | null;
  netgsm_usercode?: string | null;
  netgsm_password?: string | null;
  netgsm_header?: string | null;
  whatsapp_provider?: "log" | "netgsm" | "meta" | null;
  whatsapp_netgsm_usercode?: string | null;
  whatsapp_netgsm_password?: string | null;
  whatsapp_meta_token?: string | null;
  whatsapp_meta_phone_number_id?: string | null;
}

// Per-firm settings are addressed by firmId in the path, so they always run in
// the platform context (`platform: true`): otherwise a SUPERADMIN with an active
// company-switcher selection would have the gateway rewrite firm_id to the acting
// firm and save to the wrong tenant. A firm ADMIN never sends the header anyway.
export async function getFirmSettings(
  firmId: string,
): Promise<FirmNotificationSettings> {
  return apiFetch<FirmNotificationSettings>(
    `/api/v1/notifications/settings/${encodeURIComponent(firmId)}`,
    {},
    { platform: true },
  );
}

export async function saveFirmSettings(
  firmId: string,
  settings: FirmNotificationSettings,
): Promise<FirmNotificationSettings> {
  return apiFetch<FirmNotificationSettings>(
    `/api/v1/notifications/settings/${encodeURIComponent(firmId)}`,
    {
      method: "PUT",
      body: JSON.stringify({ ...settings, firm_id: firmId }),
    },
    { platform: true },
  );
}

// --- Own-firm profile (detailed company info; firm resolved from the JWT) ---
export interface FirmProfile {
  id: string;
  name: string;
  active: boolean;
  legal_name?: string | null;
  tax_number?: string | null;
  tax_office?: string | null;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  kvkk_contact?: string | null;
  logo_url?: string | null;
  created_at?: string;
  updated_at?: string;
}

export async function getFirmProfile(): Promise<FirmProfile> {
  return apiFetch<FirmProfile>("/api/v1/firms/profile");
}

export async function saveFirmProfile(
  patch: Partial<FirmProfile>,
): Promise<FirmProfile> {
  return apiFetch<FirmProfile>("/api/v1/firms/profile", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

// --- Platform firm registry (SUPERADMIN only) ---
// `platform: true` so the company switcher never scopes these to a single firm —
// the registry is inherently cross-firm.
export async function listFirms(): Promise<FirmProfile[]> {
  return apiFetch<FirmProfile[]>("/api/v1/firms", {}, { platform: true });
}

export async function upsertFirm(
  firm: Partial<FirmProfile> & { id: string; name: string },
): Promise<FirmProfile> {
  return apiFetch<FirmProfile>(
    "/api/v1/firms",
    {
      method: "PUT",
      body: JSON.stringify(firm),
    },
    { platform: true },
  );
}

export interface ChannelTestResult {
  channel: string;
  destination: string;
  status: "sent" | "failed";
  detail: string | null;
  adapter: string;
}

// --- Fuel validator ---
export interface FuelSummaryRow {
  vehicle_id: string;
  firm_id: string;
  sample_count: number;
  avg_lph: number;
  max_lph: number;
  min_lph: number;
}

export interface FuelSummary {
  window_minutes: number;
  history_hours: number;
  firm_id: string | null;
  vehicles: FuelSummaryRow[];
}

export interface FuelAnomaly {
  id: string;
  firm_id: string;
  vehicle_id: string;
  detected_at: string;
  baseline_lph: number;
  observed_lph: number;
  z_score: number;
  severity: string;
  alert_event_id: string | null;
}

export interface FuelScanResult {
  samples_scanned: number;
  anomalies_found: number;
  anomalies: FuelAnomaly[];
}

export async function getFuelSummary(firmId?: string): Promise<FuelSummary> {
  const q = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<FuelSummary>(`/api/v1/fuel/summary${q}`);
}

export async function listFuelAnomalies(firmId?: string): Promise<FuelAnomaly[]> {
  const q = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<FuelAnomaly[]>(`/api/v1/fuel/anomalies${q}`);
}

export async function triggerFuelScan(firmId?: string): Promise<FuelScanResult> {
  const q = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<FuelScanResult>(`/api/v1/fuel/scan${q}`, { method: "POST" });
}

// --- Reports ---
export interface FleetKpi {
  firm_id: string | null;
  generated_at: string;
  vehicles_total: number;
  fleets_total: number;
  alerts_open: number;
  alerts_critical_open: number;
  alerts_today: number;
  alerts_by_severity: Record<string, number>;
  alerts_by_status: Record<string, number>;
  fuel_anomalies_open: number;
  notifications_24h: number;
  notifications_sent_24h: number;
  notifications_failed_24h: number;
  expenses_pending: number;
  expenses_pending_total: number;
  expenses_approved_total_30d: number;
  degraded: string[];
}

export async function getFleetKpi(firmId?: string): Promise<FleetKpi> {
  const q = firmId ? `?firm_id=${encodeURIComponent(firmId)}` : "";
  return apiFetch<FleetKpi>(`/api/v1/reports/fleet-kpi${q}`);
}

// --- Routes (GPS history aggregator) ---
export interface RouteTrailPoint {
  t: string;
  lat: number;
  lon: number;
  speed_kph: number | null;
}

export interface RouteSummaryVehicle {
  vehicle_id: string;
  firm_id: string;
  point_count: number;
  distance_km: number;
  moving_km: number;
  moving_seconds: number;
  stopped_seconds: number;
  avg_speed_kph: number;
  max_speed_kph: number;
  first_seen: string | null;
  last_seen: string | null;
  bbox: { min_lat: number; max_lat: number; min_lon: number; max_lon: number } | null;
  sample_trail: RouteTrailPoint[];
}

export interface RouteFleetSummary {
  history_hours: number;
  firm_id: string | null;
  vehicles: RouteSummaryVehicle[];
}

export interface RouteTrail {
  vehicle_id: string;
  firm_id: string | null;
  history_hours: number;
  point_count: number;
  points: RouteTrailPoint[];
}

export async function getRoutesSummary(
  firmId?: string,
  hours?: number,
): Promise<RouteFleetSummary> {
  const q = new URLSearchParams();
  if (firmId) q.set("firm_id", firmId);
  if (hours != null) q.set("hours", String(hours));
  const qs = q.toString();
  return apiFetch<RouteFleetSummary>(`/api/v1/routes/summary${qs ? `?${qs}` : ""}`);
}

export async function getVehicleTrail(
  vehicleId: string,
  hours?: number,
): Promise<RouteTrail> {
  const q = new URLSearchParams();
  q.set("vehicle_id", vehicleId);
  if (hours != null) q.set("hours", String(hours));
  return apiFetch<RouteTrail>(`/api/v1/routes/trail?${q.toString()}`);
}

// --- Expenses (AICE CLERK) ---
export type ExpenseStatus = "pending" | "approved" | "rejected";

export interface ExpenseRecord {
  id: string;
  firm_id: string;
  vehicle_id: string | null;
  driver_id: string | null;
  category: string;
  amount: number;
  currency: string;
  vat_amount: number | null;
  vendor: string | null;
  occurred_at: string;
  status: ExpenseStatus;
  receipt_object_key: string | null;
  ocr_text: string | null;
  ocr_metadata: Record<string, unknown> | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
}

export interface ExpenseCreatePayload {
  firm_id: string;
  vehicle_id?: string | null;
  driver_id?: string | null;
  category: string;
  amount: number;
  currency: string;
  vat_amount?: number | null;
  vendor?: string | null;
  occurred_at: string;
  notes?: string | null;
  receipt_object_key?: string | null;
  ocr_text?: string | null;
  ocr_metadata?: Record<string, unknown> | null;
}

export interface ExpenseSummary {
  firm_id: string | null;
  since: string;
  totals_by_status: Record<string, number>;
  counts_by_status: Record<string, number>;
  totals_by_category: Record<string, number>;
  grand_total: number;
  grand_count: number;
}

export async function listExpenses(params?: {
  firm_id?: string;
  status?: ExpenseStatus;
  category?: string;
  vehicle_id?: string;
}): Promise<ExpenseRecord[]> {
  const q = new URLSearchParams();
  if (params?.firm_id) q.set("firm_id", params.firm_id);
  if (params?.status) q.set("status_filter", params.status);
  if (params?.category) q.set("category", params.category);
  if (params?.vehicle_id) q.set("vehicle_id", params.vehicle_id);
  const qs = q.toString();
  return apiFetch<ExpenseRecord[]>(`/api/v1/expenses${qs ? `?${qs}` : ""}`);
}

export async function createExpense(payload: ExpenseCreatePayload): Promise<ExpenseRecord> {
  return apiFetch<ExpenseRecord>("/api/v1/expenses", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function approveExpense(id: string, note?: string): Promise<ExpenseRecord> {
  return apiFetch<ExpenseRecord>(`/api/v1/expenses/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body: JSON.stringify({ note: note ?? null }),
  });
}

export async function rejectExpense(id: string, note?: string): Promise<ExpenseRecord> {
  return apiFetch<ExpenseRecord>(`/api/v1/expenses/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: JSON.stringify({ note: note ?? null }),
  });
}

// --- Deliveries (AICE DELIVERY) ---
export type DeliveryStatus = "pending" | "in_progress" | "completed" | "cancelled";
export type ScanStatus = "ok" | "wrong_item" | "duplicate" | "extra";

export interface ManifestItem {
  barcode: string;
  label?: string | null;
  qty: number;
}

export interface DeliveryRecord {
  id: string;
  firm_id: string;
  vehicle_id: string | null;
  driver_id: string | null;
  order_ref: string | null;
  source_label: string | null;
  destination_label: string;
  destination_lat: number | null;
  destination_lon: number | null;
  manifest: ManifestItem[];
  manifest_count: number;
  scanned_count: number;
  ok_count: number;
  status: DeliveryStatus;
  notes: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  recipient_signature_key: string | null;
  progress_pct: number;
}

export interface DeliveryScanRecord {
  id: string;
  delivery_id: string;
  barcode: string;
  status: ScanStatus;
  scanned_at: string;
  scanned_lat: number | null;
  scanned_lon: number | null;
  note: string | null;
}

export interface DeliveryCreatePayload {
  firm_id: string;
  vehicle_id?: string | null;
  driver_id?: string | null;
  order_ref?: string | null;
  source_label?: string | null;
  destination_label: string;
  destination_lat?: number | null;
  destination_lon?: number | null;
  manifest: ManifestItem[];
  notes?: string | null;
}

export async function listDeliveries(params?: {
  firm_id?: string;
  vehicle_id?: string;
  driver_id?: string;
  status?: DeliveryStatus;
}): Promise<DeliveryRecord[]> {
  const q = new URLSearchParams();
  if (params?.firm_id) q.set("firm_id", params.firm_id);
  if (params?.vehicle_id) q.set("vehicle_id", params.vehicle_id);
  if (params?.driver_id) q.set("driver_id", params.driver_id);
  if (params?.status) q.set("status_filter", params.status);
  const qs = q.toString();
  return apiFetch<DeliveryRecord[]>(`/api/v1/deliveries${qs ? `?${qs}` : ""}`);
}

export async function getDelivery(id: string): Promise<DeliveryRecord> {
  return apiFetch<DeliveryRecord>(`/api/v1/deliveries/${encodeURIComponent(id)}`);
}

export async function createDelivery(payload: DeliveryCreatePayload): Promise<DeliveryRecord> {
  return apiFetch<DeliveryRecord>("/api/v1/deliveries", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function appendDeliveryScan(
  id: string,
  barcode: string,
): Promise<DeliveryScanRecord> {
  return apiFetch<DeliveryScanRecord>(
    `/api/v1/deliveries/${encodeURIComponent(id)}/scans`,
    { method: "POST", body: JSON.stringify({ barcode }) },
  );
}

export async function listDeliveryScans(id: string): Promise<DeliveryScanRecord[]> {
  return apiFetch<DeliveryScanRecord[]>(
    `/api/v1/deliveries/${encodeURIComponent(id)}/scans`,
  );
}

export async function startDelivery(id: string): Promise<DeliveryRecord> {
  return apiFetch<DeliveryRecord>(
    `/api/v1/deliveries/${encodeURIComponent(id)}/start`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function completeDelivery(id: string, notes?: string): Promise<DeliveryRecord> {
  return apiFetch<DeliveryRecord>(
    `/api/v1/deliveries/${encodeURIComponent(id)}/complete`,
    { method: "POST", body: JSON.stringify({ notes: notes ?? null }) },
  );
}

export async function cancelDelivery(id: string, notes?: string): Promise<DeliveryRecord> {
  return apiFetch<DeliveryRecord>(
    `/api/v1/deliveries/${encodeURIComponent(id)}/cancel`,
    { method: "POST", body: JSON.stringify({ notes: notes ?? null }) },
  );
}

// --- Border (AICE BORDER) ---
export interface ThermalScanRecord {
  id: string;
  firm_id: string;
  vehicle_id: string;
  device_id: string | null;
  scanned_at: string;
  location_label: string | null;
  location_lat: number | null;
  location_lon: number | null;
  person_detected: boolean;
  max_temp_c: number | null;
  hotspot_count: number | null;
  confidence: number | null;
  image_object_key: string | null;
  alert_event_id: string | null;
  metadata_json: Record<string, unknown> | null;
  created_at: string;
}

export async function listThermalScans(params?: {
  firm_id?: string;
  vehicle_id?: string;
  person_detected?: boolean;
}): Promise<ThermalScanRecord[]> {
  const q = new URLSearchParams();
  if (params?.firm_id) q.set("firm_id", params.firm_id);
  if (params?.vehicle_id) q.set("vehicle_id", params.vehicle_id);
  if (params?.person_detected !== undefined)
    q.set("person_detected", String(params.person_detected));
  const qs = q.toString();
  return apiFetch<ThermalScanRecord[]>(`/api/v1/border/scans${qs ? `?${qs}` : ""}`);
}

export async function getThermalScanReport(id: string): Promise<{
  scan: ThermalScanRecord;
  summary: string;
  generated_at: string;
}> {
  return apiFetch(`/api/v1/border/scans/${encodeURIComponent(id)}/report`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// --- Driver (AICE DRIVER) ---
export type DriverSeverity = "medium" | "high" | "critical";

export interface DriverStateRecord {
  vehicle_id: string;
  firm_id: string;
  driver_id: string | null;
  perclos_pct: number;
  microsleep_risk: number;
  yawns_15min: number;
  head_drops_15min: number;
  stress_score: number | null;
  aggressive_score: number | null;
  eye_closure_seconds: number | null;
  alert_severity: DriverSeverity | null;
  updated_at: string;
}

export async function listDriverStates(params?: {
  firm_id?: string;
  risky_only?: boolean;
}): Promise<DriverStateRecord[]> {
  const q = new URLSearchParams();
  if (params?.firm_id) q.set("firm_id", params.firm_id);
  if (params?.risky_only) q.set("risky_only", "true");
  const qs = q.toString();
  return apiFetch<DriverStateRecord[]>(`/api/v1/drivers/states${qs ? `?${qs}` : ""}`);
}

export async function getDriverState(vehicleId: string): Promise<DriverStateRecord> {
  return apiFetch<DriverStateRecord>(
    `/api/v1/drivers/state/${encodeURIComponent(vehicleId)}`,
  );
}

export async function getDriverHistory(
  vehicleId: string,
  limit = 50,
): Promise<DriverStateRecord[]> {
  return apiFetch<DriverStateRecord[]>(
    `/api/v1/drivers/state/${encodeURIComponent(vehicleId)}/history?limit=${limit}`,
  );
}

// --- Stream (Canlı AI Görüntü) ---
export interface StreamConfig {
  mode: "live" | "mock";
  livekit_url: string;
  notice: string | null;
}

export interface StreamToken {
  token: string;
  url: string;
  room: string;
  identity: string;
  expires_at: string;
  mode: "live" | "mock";
}

export interface StreamRoom {
  name: string;
  firm_id: string;
  vehicle_id: string | null;
  description: string | null;
  created_at: string;
  active: boolean;
}

export async function getStreamConfig(): Promise<StreamConfig> {
  return apiFetch<StreamConfig>("/api/v1/streams/config");
}

export async function issueStreamToken(payload: {
  room: string;
  identity: string;
  name?: string;
  can_subscribe?: boolean;
  can_publish?: boolean;
}): Promise<StreamToken> {
  return apiFetch<StreamToken>("/api/v1/streams/token", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listStreamRooms(params?: {
  firm_id?: string;
  vehicle_id?: string;
}): Promise<StreamRoom[]> {
  const q = new URLSearchParams();
  if (params?.firm_id) q.set("firm_id", params.firm_id);
  if (params?.vehicle_id) q.set("vehicle_id", params.vehicle_id);
  const qs = q.toString();
  return apiFetch<StreamRoom[]>(`/api/v1/streams/rooms${qs ? `?${qs}` : ""}`);
}

// --- LiveKit system settings (ADMIN; UI-configurable, DB over env) ---
export interface StreamSettings {
  livekit_url?: string | null;
  livekit_api_key?: string | null;
  livekit_api_secret?: string | null;
  token_ttl_seconds?: number | null;
  // read-only, server-computed
  mode?: "live" | "mock" | null;
  source?: "db" | "env" | "none" | null;
}

export async function getStreamSettings(): Promise<StreamSettings> {
  return apiFetch<StreamSettings>("/api/v1/streams/settings");
}

export async function saveStreamSettings(
  settings: StreamSettings,
): Promise<StreamSettings> {
  return apiFetch<StreamSettings>("/api/v1/streams/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

// --- Receipt storage (MinIO/S3) system settings (ADMIN; DB over env) ---
export interface StorageSettings {
  minio_endpoint?: string | null;
  minio_access_key?: string | null;
  minio_secret_key?: string | null;
  minio_bucket?: string | null;
  minio_secure?: boolean | null;
  receipt_url_ttl_seconds?: number | null;
  // read-only, server-computed
  configured?: boolean | null;
  source?: "db" | "env" | "none" | null;
}

export async function getStorageSettings(): Promise<StorageSettings> {
  return apiFetch<StorageSettings>("/api/v1/expenses/settings/storage");
}

export async function saveStorageSettings(
  settings: StorageSettings,
): Promise<StorageSettings> {
  return apiFetch<StorageSettings>("/api/v1/expenses/settings/storage", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export async function testStorageSettings(): Promise<{
  status: string;
  detail: string;
}> {
  return apiFetch<{ status: string; detail: string }>(
    "/api/v1/expenses/settings/storage/test",
    { method: "POST", body: JSON.stringify({}) },
  );
}

export async function reocrExpense(id: string): Promise<ExpenseRecord> {
  return apiFetch<ExpenseRecord>(`/api/v1/expenses/${encodeURIComponent(id)}/reocr`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export interface ReceiptOcrSuggestion {
  date_iso: string | null;
  total_amount: number | null;
  currency: string | null;
  vat_amount: number | null;
  vat_percent: number | null;
  vendor: string | null;
  category_hint: string | null;
  raw_text_length: number;
}

export interface ReceiptUploadResult {
  object_key: string;
  content_type: string;
  size: number;
  presigned_url: string | null;
  ocr_text: string | null;
  ocr: ReceiptOcrSuggestion | null;
}

export async function uploadReceipt(file: File): Promise<ReceiptUploadResult> {
  // Multipart — we deliberately do NOT set Content-Type; the browser will
  // produce the multipart boundary. apiFetch is built around JSON so we go
  // through fetch directly here.
  const token = getToken();
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/v1/expenses/receipts", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (res.status === 401) {
    setToken(null);
    throw new ApiError(401, "session expired");
  }
  if (!res.ok) {
    throw new ApiError(res.status, await res.text());
  }
  return (await res.json()) as ReceiptUploadResult;
}

export interface ReceiptUrl {
  object_key: string;
  url: string;
  expires_in: number;
}

export async function getReceiptUrl(expenseId: string): Promise<ReceiptUrl> {
  return apiFetch<ReceiptUrl>(
    `/api/v1/expenses/${encodeURIComponent(expenseId)}/receipt-url`,
  );
}

/** Fetches the receipt bytes through the gateway (JWT-protected) and returns
 * a blob URL the caller can open in a new tab. The presigned URL alternative
 * requires MinIO to be publicly addressable, which our infra doesn't (yet)
 * provide — the bytes are tunneled through the API instead. */
// --- System health (public; aggregates all backend services) ---
export interface ServiceHealth {
  service: string;
  status: "up" | "down" | "timeout" | "unconfigured" | "unknown";
  latency_ms: number | null;
  detail: string | null;
}

export interface SystemHealth {
  status: "up" | "degraded" | "unknown";
  checked_at: string;
  services: ServiceHealth[];
}

export async function getSystemHealth(): Promise<SystemHealth> {
  // Public endpoint — does not require a token. We still go through apiFetch
  // so a stale token gets cleared if it somehow rejects.
  const res = await fetch("/api/v1/health/system");
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return (await res.json()) as SystemHealth;
}

export async function fetchReceiptBlobUrl(expenseId: string): Promise<string> {
  const token = getToken();
  const res = await fetch(
    `/api/v1/expenses/${encodeURIComponent(expenseId)}/receipt-data`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (res.status === 401) {
    setToken(null);
    throw new ApiError(401, "session expired");
  }
  if (!res.ok) {
    throw new ApiError(res.status, await res.text());
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function getExpenseSummary(firmId?: string, days?: number): Promise<ExpenseSummary> {
  const q = new URLSearchParams();
  if (firmId) q.set("firm_id", firmId);
  if (days != null) q.set("days", String(days));
  const qs = q.toString();
  return apiFetch<ExpenseSummary>(`/api/v1/expenses/summary${qs ? `?${qs}` : ""}`);
}

export async function testFirmChannel(
  firmId: string,
  channel: "email" | "sms" | "whatsapp" | "panel",
  destination: string,
  subject?: string,
  message?: string,
): Promise<ChannelTestResult> {
  return apiFetch<ChannelTestResult>(
    `/api/v1/notifications/settings/${encodeURIComponent(firmId)}/test`,
    {
      method: "POST",
      body: JSON.stringify({ channel, destination, subject, message }),
    },
    { platform: true },
  );
}

// --- Invitation acceptance (public; the single-use token is the credential) ---
export interface InvitePreview {
  username: string;
  display_name: string | null;
  firm_id: string;
  firm_name: string | null;
}

export async function getInvite(token: string): Promise<InvitePreview> {
  // Public endpoint — the invitee has no JWT yet. Bypass the auth header path.
  const res = await fetch(`/api/v1/auth/invite/${encodeURIComponent(token)}`);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return (await res.json()) as InvitePreview;
}

export async function acceptInvite(
  token: string,
  newPassword: string,
): Promise<void> {
  const res = await fetch("/api/v1/auth/accept-invite", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, new_password: newPassword }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
}

export async function changePassword(
  oldPassword: string,
  newPassword: string,
): Promise<void> {
  await apiFetch<void>("/api/v1/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  });
}

export interface MeRecord {
  user_id: string;
  firm_id: string;
  role: string;
  exp: number;
}

export async function whoami(): Promise<MeRecord> {
  return apiFetch<MeRecord>("/api/v1/auth/me");
}

export function parseTokenClaims(): { user_id: string; firm_id: string; role: UserRole } | null {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return { user_id: payload.sub, firm_id: payload.firm_id, role: payload.role };
  } catch {
    return null;
  }
}
